import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class BigCommerceSettings extends ModelObject<BigCommerceSettings> {

    public version: number = 0;
    /**
    * Store hash found from the api path 'https://api.bigcommerce.com/stores/[storehash]/v3/'
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public storeHash: string = "";
    /**
    * Access token for making requests to Big Commerce
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public accessToken: string = "";
    private _outletId: Guid | null = null;
    /**
    * Outlet Id for all Big Commerce order imports.
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    /**
    * Whether the current integration should run continuously in the background.
    */
    public runInBackground: boolean = false;
    private _ignoreBeforeDate: LocalDate | null = null;
    /**
    * If provided then Orders on BigCommerce from before this date will be ignored.
    */
    public get ignoreBeforeDate(): LocalDate | null { return this._ignoreBeforeDate }
    public set ignoreBeforeDate(value: LocalDate | null) {
      this._ignoreBeforeDate = value === undefined ? null : value;
    }
    private _defaultDeliveryStockItemId: Guid | null = null;
    /**
    * Default Stock Item Id to be used for delivery charge of imported orders. This will be used if no BrewMan product name matches the Shipping Title or Code in BigCommerce
    */
    public get defaultDeliveryStockItemId(): Guid | null { return this._defaultDeliveryStockItemId }
    public set defaultDeliveryStockItemId(value: Guid | null) {
      this._defaultDeliveryStockItemId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.storeHash = jsonSerializedObject.storeHash;
          this.accessToken = jsonSerializedObject.accessToken;
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.runInBackground = jsonSerializedObject.runInBackground;
          this.ignoreBeforeDate = jsonSerializedObject.ignoreBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.ignoreBeforeDate);
          this.defaultDeliveryStockItemId = jsonSerializedObject.defaultDeliveryStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultDeliveryStockItemId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.storeHash = this.storeHash;
        toRet.accessToken = this.accessToken;
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.runInBackground = this.runInBackground;
        toRet.ignoreBeforeDate = this.ignoreBeforeDate == null ? null : this.ignoreBeforeDate.toString();
        toRet.defaultDeliveryStockItemId = this.defaultDeliveryStockItemId == null ? null : this.defaultDeliveryStockItemId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "BigCommerceSettings.OutletId cannot be empty"},
            {field: "defaultDeliveryStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "BigCommerceSettings.DefaultDeliveryStockItemId cannot be empty"},
    ];
}


export class BigCommerceSettingsService extends ServiceBase {

  public async getBigCommerceSettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<BigCommerceSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new BigCommerceSettings(response.bigCommerceSettings);;
    return this.makeJsonRequest("bigCommerceSettings/v1/getBigCommerceSettings", req, false, extractor, opts);
  };

  public async saveBigCommerceSettings(bigCommerceSettings: BigCommerceSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    bigCommerceSettings:bigCommerceSettings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("bigCommerceSettings/v1/saveBigCommerceSettings", req, true, extractor, opts);
  };

  public async hardDeleteBigCommerceSettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("bigCommerceSettings/v1/hardDeleteBigCommerceSettings", req, true, extractor, opts);
  };

  public async testBigCommerceConnection(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<boolean> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.isConnected;;
    return this.makeJsonRequest("bigCommerceSettings/v1/testBigCommerceConnection", req, false, extractor, opts);
  };
}

export const bigCommerceSettingsService = new BigCommerceSettingsService();